import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LINKS, ASSETS } from "../../lib/constants";

const NAV = [
  { to: "/", label: "Home", external: false },
  { to: "/about", label: "About", external: false },
  { to: "/work-with-me", label: "Work With Me", external: false },
  { to: "/follow-the-cash", label: "Follow The Cash", external: false },
  { to: LINKS.freeAssessment, label: "Free Reality Check", external: true },
  { to: "/contact", label: "Contact", external: false },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const linkClass = ({ isActive }) =>
    `relative font-sans text-[14px] font-semibold uppercase tracking-[0.12em] transition-colors ${
      isActive ? "text-brand-black" : "text-brand-stone hover:text-brand-black"
    }`;

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all duration-300 bg-white ${
        scrolled ? "shadow-[0_1px_0_rgba(0,0,0,0.08)]" : "border-b border-black/5"
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-5 sm:px-8 lg:px-10 xl:px-12 h-[116px] flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center shrink-0">
          <img
            src={ASSETS.logo}
            alt="ProfitMIND"
            className="block h-[88px] w-auto rounded-2xl"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-4 xl:gap-7">
          {NAV.map((n) =>
            n.external ? (
              <a
                key={n.label}
                href={n.to}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="relative font-sans text-[14px] font-semibold uppercase tracking-[0.12em] text-brand-stone hover:text-brand-black transition-colors"
              >
                {n.label}
              </a>
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={linkClass}
                end={n.to === "/"}
              >
                {({ isActive }) => (
                  <>
                    {n.label}
                    {isActive && (
                      <span className="absolute -bottom-2 left-0 right-0 h-[3px] bg-brand-yellow" />
                    )}
                  </>
                )}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:block shrink-0">
          <a
            href={LINKS.tidycal}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-book-call-btn"
            className="btn-yellow shadow-y-hover !py-3 !px-5 !text-[13px]"
          >
            Book a free chat
          </a>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 -mr-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-black/10 bg-white">
          <div className="max-w-screen-2xl mx-auto px-5 py-6 flex flex-col gap-4">
            {NAV.map((n) =>
              n.external ? (
                <a
                  key={n.label}
                  href={n.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-display text-2xl uppercase text-brand-stone"
                >
                  {n.label}
                </a>
              ) : (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `font-display text-2xl uppercase ${isActive ? "text-brand-black" : "text-brand-stone"}`
                  }
                  end={n.to === "/"}
                >
                  {n.label}
                </NavLink>
              )
            )}
            <a
              href={LINKS.tidycal}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-book-call-btn"
              className="btn-yellow shadow-y-hover mt-2 self-start"
            >
              Book a free chat
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
